/**
 * conciliacionApi — cliente de la conciliación diaria por PSP expuesta por payment.
 *
 *   GET  /api/conciliacion?period=YYYY-MM-DD&psp=virtualpos  — conciliación de un día puntual
 *   POST /api/reconsulta-cobro { chargeId }                   — reconsulta puntual (botón por fila)
 *   GET  /api/reconsulta-cobro?external_charge_id=...         — reconsulta puntual (buscador)
 *   POST /api/unmatched-resolver { id, nota }                 — cierra un descuadre como investigado
 *
 * Los tres endpoints ya corren en payment (conciliacion.worker.ts,
 * reconsulta-liquidacion.service.ts vía api/reconsulta-cobro.ts, y
 * api/unmatched-resolver.ts) y aceptan JWT de sesión de Clerk de un staff Yool
 * (@yooldigital.com) además de SERVICE_SECRET — por eso, igual que en
 * diaApi.js, todas las funciones reciben `token` como primer parámetro: es el
 * mismo token que ya usan las pantallas del ciclo diario.
 *
 * `period` en `getConciliacionDelDia` acepta 'YYYY-MM' (mes) o 'YYYY-MM-DD'
 * (día, spec §10.3) — el día es el formato que usa esta pantalla.
 *
 * Mismo patrón que diaApi.js: JWT de staff Clerk, URL vía baseUrl.js
 * (same-origin → proxy api/yoolpay/api/[endpoint].js), y el error se lanza
 * como `Error(mensaje)` sin `err.status` — convención del repo.
 *
 * @see src/services/yoolpay/diaApi.js — cliente hermano (patrón copiado)
 * @see payment/api/conciliacion.ts, api/reconsulta-cobro.ts, api/unmatched-resolver.ts
 */
import { yoolpayBaseUrl } from './baseUrl.js';

const BASE = yoolpayBaseUrl();

async function call(path, token, { method = 'GET', body } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.error || `payment respondió ${res.status}`);
  return payload;
}

const qs = (params) =>
  new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== ''),
  ).toString();

/** Conciliación de un día puntual — spec §10.3. */
export const getConciliacionDelDia = (token, fecha, psp) =>
  call(`/api/conciliacion?${qs({ period: fecha, psp })}`, token);

/**
 * Botón manual por fila — spec §10.4.1. `payment` deriva la ruta PSP
 * (one_time vs subscription) de `subscriptions.orden_pago_id`; este
 * cliente no manda `tipo` — mandarlo fijo fue el bug que dejaba pagos
 * únicos sin reconsultar (ver reconsulta-liquidacion.service.ts).
 */
export const reconsultarCobro = (token, chargeId) =>
  call('/api/reconsulta-cobro', token, {
    method: 'POST',
    body: { chargeId },
  });

/** Buscador independiente, sin fecha — spec §10.4.2. */
export const buscarCobro = (token, externalChargeId) =>
  call(`/api/reconsulta-cobro?${qs({ external_charge_id: externalChargeId })}`, token);

/** Cerrar un descuadre como investigado — spec §10.5. */
export const resolverDescuadre = (token, id, nota) =>
  call('/api/unmatched-resolver', token, {
    method: 'POST',
    body: { id, nota },
  });
